/**
 * @fileoverview Dedicated performance-by-product page with full table view.
 * @module features/home/pages/PerformanceByProductPage
 */

import { ReactElement, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { DashboardDataProvider } from '@/shared/providers'
import { PerformanceBySkuTable } from '@/shared/components/ui'
import { useFilterMenuSlot } from '@/shared/contexts/FilterMenuSlotContext'
import { TagsFilterExtension } from '@/shared/components/filters/extensions/TagsFilterExtension'
import { DateRangeHeading } from '@/shared/components/filters/DateRangeHeading'
import { Button } from '@/components/ui/button'
import { Kbd } from '@/components/ui/kbd'

/**
 * Standalone page for the performance-by-product table.
 * Provides DashboardDataProvider context and uses the global FlyingMenu
 * (mounted via App.tsx allowlist).
 */
export function PerformanceByProductPage(): ReactElement {
  const navigate = useNavigate()
  const { setSlot, clearSlot } = useFilterMenuSlot()

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.preventDefault()
        navigate('/dashboard')
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [navigate])

  // Inject the tag filter into the global FlyingMenu slot. Selecting a tag
  // updates FilterContext.tags, which DashboardDataProvider threads into the
  // PRODUCT-dimension statistics queries feeding PerformanceBySkuTable below.
  useEffect(() => {
    setSlot({ content: <TagsFilterExtension /> })
    return () => clearSlot()
  }, [setSlot, clearSlot])

  return (
    <DashboardDataProvider>
      <div className="min-h-screen">
        <div className="max-w-screen-xl mx-auto px-4 lg:px-8 py-8">
          <div className="flex items-center gap-4 mb-6">
            <Button
              variant="ghost"
              onClick={() => navigate('/dashboard')}
              className="gap-2"
            >
              <ArrowLeft className="h-5 w-5" />
              <Kbd>Esc</Kbd>
            </Button>
            {/* Orientation anchor: names the active date-filter range */}
            <DateRangeHeading className="ml-auto" />
          </div>

          <PerformanceBySkuTable />
        </div>
      </div>
    </DashboardDataProvider>
  )
}
